/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@chakra-ui/react";
import { ImSpinner8 } from "react-icons/im";

type PayoutMethod = {
  asset: string;
  crypto: boolean;
};

const AccountPayoutMethodsTab: React.FC<{
  detail: any;
  setSwalProps: any;
  fetchAccountDetail: any;
}> = ({ detail, setSwalProps, fetchAccountDetail }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [payoutMethodList, setPayoutMethodList] = useState<PayoutMethod[]>([]);
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  const [originalMethods, setOriginalMethods] = useState<string[]>([]);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const username = localStorage.getItem("username");

  useEffect(() => {
    fetchPayoutMethods();
  }, []);

  useEffect(() => {
    // Initialize selected methods when detail is available
    if (detail) {
      let methods: string[] = [];

      if (detail.payout_methods) {
        if (Array.isArray(detail.payout_methods)) {
          // Handle array of strings or array of objects with asset
          methods = detail.payout_methods
            .map((m: any) => (typeof m === "string" ? m : m?.asset))
            .filter(Boolean);
        } else if (typeof detail.payout_methods === "object") {
          // Handle object where keys are UUIDs and values have asset property
          // Extract unique asset values
          const assetSet = new Set<string>();
          Object.values(detail.payout_methods).forEach((m: any) => {
            if (m?.asset) assetSet.add(m.asset);
          });
          methods = Array.from(assetSet);
        }
      }

      setSelectedMethods(methods);
      setOriginalMethods(methods);
    }
  }, [detail]);

  const fetchPayoutMethods = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/subaccount/payout-methods`
      );
      const methods = response.data.payoutmethods?.payload || [];
      setPayoutMethodList(methods);
    } catch (error) {
      console.error("Error fetching payout assets", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayoutMethodChange = (asset: string, checked: boolean) => {
    setSelectedMethods((prev) => {
      if (checked) {
        return [...prev, asset];
      } else {
        return prev.filter((a) => a !== asset);
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedMethods.length === payoutMethodList.length) {
      setSelectedMethods([]);
    } else {
      setSelectedMethods(payoutMethodList.map((m) => m.asset));
    }
  };

  const handleSubmit = async () => {
    // Compute diff
    const add_payout_methods = selectedMethods.filter(
      (asset) => !originalMethods.includes(asset)
    );
    const delete_payout_methods = originalMethods.filter(
      (asset) => !selectedMethods.includes(asset)
    );

    // Only call API if there are changes
    if (add_payout_methods.length === 0 && delete_payout_methods.length === 0) {
      setSwalProps({
        show: true,
        icon: "info",
        title: "No Changes",
        html: "No changes to save",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
      return;
    }

    const postData = {
      id: detail.id,
      add_payout_methods,
      delete_payout_methods,
      username,
      userRole,
    };

    try {
      setIsSaving(true);
      await axios
        .post(
          process.env.REACT_APP_API_URL +
            `/api/${userRole}/subaccount/action/update-subaccount`,
          postData
        )
        .then(function (response) {
          if (response && response.data?.status === "ok") {
            // Update original to reflect saved state
            setOriginalMethods([...selectedMethods]);
            // Refresh account detail to sync with server
            fetchAccountDetail(detail.id, true);
            setSwalProps({
              show: true,
              icon: "success",
              title: "Success",
              html: "Payout assets have been successfully updated",
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsSaving(false);
              },
            });
          } else {
            setIsSaving(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsSaving(false);
              },
            });
          }
        })
        .catch(function (error) {
          setIsSaving(false);
          setSwalProps({
            show: true,
            icon: "error",
            title: "Oops!",
            html: error.response?.data?.message || "Something went wrong",
            showConfirmButton: true,
            didClose: () => {
              setSwalProps({});
              setIsSaving(false);
            },
          });
        });
    } catch (error) {
      setIsSaving(false);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          setIsSaving(false);
        },
      });
    }
  };

  // Separate fiat and crypto methods
  const fiatMethods = payoutMethodList.filter((m) => !m.crypto);
  const cryptoMethods = payoutMethodList.filter((m) => m.crypto);

  return (
    <div className="px-4 py-8">
      {detail !== null ? (
        <>
          <div className="space-y-4 pb-8">
            {!isLoading && payoutMethodList.length > 0 && (
              <div className="flex items-center justify-start">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  disabled={isSaving}
                  className="text-xs font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50"
                >
                  {selectedMethods.length === payoutMethodList.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>
            )}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <ImSpinner8
                  className="spinner text-primary-500"
                  style={{ fontSize: "1.8rem" }}
                />
              </div>
            ) : payoutMethodList.length === 0 ? (
              <p className="text-sm text-gray-500">
                No payout assets available
              </p>
            ) : (
              <>
                {payoutMethodList.filter((m) => !m.crypto).length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-navy-700">
                      Fiat
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6">
                      {payoutMethodList
                        .filter((m) => !m.crypto)
                        .map((method) => (
                          <label
                            key={method.asset}
                            className={`flex cursor-pointer flex-col rounded-lg border p-3 transition-all ${
                              selectedMethods.includes(method.asset)
                                ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            } ${isSaving ? "cursor-not-allowed opacity-60" : ""}`}
                          >
                            <div className="flex items-start space-x-2">
                              <input
                                type="checkbox"
                                checked={selectedMethods.includes(method.asset)}
                                onChange={(e) =>
                                  handlePayoutMethodChange(
                                    method.asset,
                                    e.target.checked
                                  )
                                }
                                disabled={isSaving}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <div className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-navy-700">
                                  {method.asset}
                                </span>
                              </div>
                            </div>
                          </label>
                        ))}
                    </div>
                  </div>
                )}
                {payoutMethodList.filter((m) => m.crypto).length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-navy-700">
                      Crypto
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6">
                      {payoutMethodList
                        .filter((m) => m.crypto)
                        .map((method) => (
                          <label
                            key={method.asset}
                            className={`flex cursor-pointer flex-col rounded-lg border p-3 transition-all ${
                              selectedMethods.includes(method.asset)
                                ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            } ${isSaving ? "cursor-not-allowed opacity-60" : ""}`}
                          >
                            <div className="flex items-start space-x-2">
                              <input
                                type="checkbox"
                                checked={selectedMethods.includes(method.asset)}
                                onChange={(e) =>
                                  handlePayoutMethodChange(
                                    method.asset,
                                    e.target.checked
                                  )
                                }
                                disabled={isSaving}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <div className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-navy-700">
                                  {method.asset}
                                </span>
                              </div>
                            </div>
                          </label>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {!isLoading && selectedMethods.length > 0 && (
              <p className="text-xs text-gray-500">
                {selectedMethods.length} payout asset
                {selectedMethods.length > 1 ? "s" : ""} selected
              </p>
            )}
          </div>

          {!isLoading && (
            <div className="flex flex-wrap justify-end pt-2">
              <Button
                onClick={() => handleSubmit()}
                isDisabled={
                  isSaving ||
                  (selectedMethods.length === originalMethods.length &&
                    selectedMethods.every((id) => originalMethods.includes(id)))
                }
                isLoading={isSaving}
                loadingText="Updating Payout Assets..."
                className="btn-primary relative overflow-hidden rounded-md px-8 py-2.5 font-semibold text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="relative z-10 tracking-tight">
                  Update Payout Assets
                </span>
                <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="flex items-center justify-center py-12">
          <ImSpinner8
            className="spinner text-primary-500"
            style={{ fontSize: "1.8rem" }}
          />
        </div>
      )}
    </div>
  );
};

export default AccountPayoutMethodsTab;
